import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { useDragReorder } from "@/hooks/useDragReorder";
import TranslateButton from "@/components/TranslateButton";
import AiSuggestButton from "@/components/AiSuggestButton";
import ImageUploader, { resolveAssetUrl } from "@/components/ImageUploader";

const EMPTY = {
  id: "",
  title_en: "",
  title_ar: "",
  tagline_en: "",
  tagline_ar: "",
  icon: "BookOpen",
  hero_image: "",
  sections: [],
};

export default function AdminEncyclopediaTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: enc, isLoading } = useQuery({ queryKey: ["encyclopedia"], queryFn: endpoints.encyclopedia });
  const [editing, setEditing] = useState(null);

  const modules = enc?.modules || [];

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["encyclopedia"] });
  };

  const onSave = async (form) => {
    if (!editing) return;
    try {
      // Parse sections JSON
      let sections = form.sections;
      if (typeof sections === "string") {
        try {
          sections = JSON.parse(sections || "[]");
        } catch {
          toast.error(isRTL ? "صياغة JSON غير صحيحة في الأقسام" : "Invalid JSON in sections");
          return;
        }
      }
      const payload = { ...form, sections };
      if (editing.id === "new") {
        await endpoints.adminAddEncycModule(payload);
        toast.success(isRTL ? "تمت الإضافة" : "Added");
      } else {
        await endpoints.adminUpdateEncycModule(editing.id, payload);
        toast.success(isRTL ? "تم الحفظ" : "Saved");
      }
      refetch();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذه الوحدة؟" : "Delete this module?")) return;
    try {
      await endpoints.adminDeleteEncycModule(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const move = async (ids) => {
    try {
      await endpoints.adminReorderEncyclopedia(ids);
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الترتيب" : "Reorder failed");
    }
  };

  const { getDragProps } = useDragReorder(modules, move);

  if (isLoading) return <div className="text-[#5C544F]">…</div>;

  return (
    <div data-testid="admin-encyclopedia-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-[#2A2421]">
          {isRTL ? "الموسوعة" : "Encyclopedia"} · {modules.length}
        </h2>
        <Button
          data-testid="enc-add-module-btn"
          onClick={() => setEditing({ id: "new", data: EMPTY })}
          className="bg-[#2A2421] text-[#F9F6F0] hover:bg-[#3A3030]"
        >
          <Plus className="w-4 h-4 me-1" /> {isRTL ? "وحدة جديدة" : "New module"}
        </Button>
      </div>

      <p className="text-[10px] text-[#5C544F] mb-3">
        {isRTL ? "اسحب البطاقة من مقبض ⋮⋮ لإعادة الترتيب" : "Drag the ⋮⋮ handle to reorder"}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((m) => (
          <div
            key={m.id}
            {...getDragProps(m.id)}
            className="rounded-2xl border border-[#2A2421]/10 bg-white p-4 flex gap-3 transition-all data-[dragging=true]:opacity-50 data-[drag-over=true]:border-[#C86240] data-[drag-over=true]:bg-[#C86240]/5"
            data-testid={`enc-module-${m.id}`}
          >
            <div className="flex items-center text-[#5C544F] hover:text-[#2A2421] cursor-grab active:cursor-grabbing">
              <GripVertical className="w-4 h-4" />
            </div>
            {m.hero_image ? (
              <img src={resolveAssetUrl(m.hero_image)} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-[#F9F6F0] flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#2A2421] truncate">{isRTL ? m.title_ar : m.title_en}</div>
              <div className="text-xs text-[#5C544F] truncate">{isRTL ? m.tagline_ar : m.tagline_en}</div>
              <div className="text-[10px] text-[#5C544F] mt-1">{(m.sections || []).length} {isRTL ? "أقسام" : "sections"}</div>
              <div className="flex items-center gap-1 mt-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ id: m.id, data: m })} data-testid={`enc-edit-${m.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(m.id)} data-testid={`enc-delete-${m.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ModuleDialog
          key={editing.id}
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          isRTL={isRTL}
        />
      )}
    </div>
  );
}

function ModuleDialog({ editing, onClose, onSave, isRTL }) {
  const d = editing.data || EMPTY;
  const [form, setForm] = useState({ ...EMPTY, ...d, sections: JSON.stringify(d.sections || [], null, 2) });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="enc-module-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id === "new" ? (isRTL ? "وحدة جديدة" : "New module") : (isRTL ? "تعديل الوحدة" : "Edit module")}</DialogTitle>
          <DialogDescription className="sr-only">
            {isRTL ? "نموذج تعديل وحدة الموسوعة" : "Encyclopedia module editor"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#C86240]/8 via-white to-[#F9F6F0] border border-[#C86240]/15 flex items-center justify-between gap-3" data-testid="enc-ai-suggest-banner">
            <div className="text-xs text-[#5C544F] flex-1">
              {isRTL
                ? "اقترح عنواناً ووصفاً بالذكاء الاصطناعي من محتوى الأقسام."
                : "Suggest a bilingual title & tagline from the sections content."}
            </div>
            <AiSuggestButton
              getContent={() => {
                try {
                  const arr = typeof form.sections === "string" ? JSON.parse(form.sections || "[]") : (form.sections || []);
                  return arr.map((s) => `${s.title_en || ""} ${s.title_ar || ""} ${s.body_en || ""} ${s.body_ar || ""}`).join("\n").trim();
                } catch { return ""; }
              }}
              onSuggested={(d) => setForm((f) => ({
                ...f,
                title_en: f.title_en || d.title_en,
                title_ar: f.title_ar || d.title_ar,
                tagline_en: f.tagline_en || d.summary_en,
                tagline_ar: f.tagline_ar || d.summary_ar,
              }))}
              testid="enc-ai-suggest-btn"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldWithTranslate label={isRTL ? "العنوان (عربي)" : "Title (AR)"} value={form.title_ar} onChange={set("title_ar")} testid="enc-title-ar" sourceText={form.title_en} target="ar" onTranslated={(v) => setForm((f) => ({ ...f, title_ar: v }))} />
            <FieldWithTranslate label={isRTL ? "العنوان (إنجليزي)" : "Title (EN)"} value={form.title_en} onChange={set("title_en")} testid="enc-title-en" sourceText={form.title_ar} target="en" onTranslated={(v) => setForm((f) => ({ ...f, title_en: v }))} />
            <FieldWithTranslate label={isRTL ? "وصف مختصر (عربي)" : "Tagline (AR)"} value={form.tagline_ar} onChange={set("tagline_ar")} testid="enc-tagline-ar" sourceText={form.tagline_en} target="ar" onTranslated={(v) => setForm((f) => ({ ...f, tagline_ar: v }))} />
            <FieldWithTranslate label={isRTL ? "وصف مختصر (إنجليزي)" : "Tagline (EN)"} value={form.tagline_en} onChange={set("tagline_en")} testid="enc-tagline-en" sourceText={form.tagline_ar} target="en" onTranslated={(v) => setForm((f) => ({ ...f, tagline_en: v }))} />
            <Field label={isRTL ? "أيقونة (Lucide)" : "Icon (Lucide)"} value={form.icon} onChange={set("icon")} testid="enc-icon" />
          </div>
          <ImageUploader
            label={isRTL ? "صورة الغلاف (Hero)" : "Hero image"}
            value={form.hero_image}
            onChange={(url) => setForm((f) => ({ ...f, hero_image: url }))}
            testid="enc-hero-uploader"
            aspect="video"
          />
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C544F]">
              {isRTL ? "الأقسام (JSON)" : "Sections (JSON)"}
            </label>
            <Textarea
              value={form.sections}
              onChange={set("sections")}
              rows={14}
              className="font-mono text-xs"
              data-testid="enc-sections-json"
            />
            <p className="text-[10px] text-[#5C544F] mt-1">
              {isRTL ? "مصفوفة JSON من الأقسام. كل قسم يحتوي على title_en, title_ar, body_en, body_ar إلخ." : "JSON array of sections. Each has title_en, title_ar, body_en, body_ar, etc."}
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="enc-cancel-btn">{isRTL ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => onSave(form)} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="enc-save-btn">
              {isRTL ? "حفظ" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>
      <Input value={value || ""} onChange={onChange} data-testid={testid} />
    </div>
  );
}

function FieldWithTranslate({ label, value, onChange, testid, sourceText, target, onTranslated }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>
        <TranslateButton sourceText={sourceText} target={target} onTranslated={onTranslated} testid={`${testid}-translate`} />
      </div>
      <Input value={value || ""} onChange={onChange} data-testid={testid} />
    </div>
  );
}
